import PhoneInput, { Props as PhoneInputProps, parsePhoneNumber } from 'react-phone-number-input/input';
import { InputHTMLAttributes, Ref, forwardRef, useMemo } from 'react';
import { useField } from 'formik';

import FormTextField from '../FormTextField';
import { FormTextFieldProps } from '../FormTextField/FormTextField';

export interface PhoneNumberInputProps extends Partial<PhoneInputProps> {
  formikField: string;
  mobileTextFieldProps?: Partial<FormTextFieldProps>;
  submitting?: boolean;
}

type RequestCodeInputProps = InputHTMLAttributes<HTMLInputElement> & PhoneNumberInputProps;

function RequestCodeInput(
  { value, submitting, onChange, formikField, mobileTextFieldProps }: RequestCodeInputProps,
  ref: Ref<HTMLInputElement>
) {
  return (
    <FormTextField
      key="gnron940"
      ref={ref}
      fieldId={formikField}
      fullWidth
      type="tel"
      name="mobileNumber"
      id="formatted-phone-number-input"
      label="Mobile number"
      value={value}
      disabled={submitting}
      placeholder="0412 345 678"
      onChange={onChange}
      {...mobileTextFieldProps}
    />
  );
}

const CodeInputForwardRef = forwardRef<HTMLInputElement, RequestCodeInputProps>(
  RequestCodeInput
) as PhoneInputProps['inputComponent'];

/**
 * A wrapper for the PhoneNumberInput component, supporting the MUI TextField.
 * @remarks Must be wrapped in a Formik provider.
 */
export default function PhoneNumberInput({ formikField, ...rest }: PhoneNumberInputProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [{ value }, _meta, { setValue }] = useField(formikField);

  const phoneNumberValue = value ?? '';

  // The PhoneInput component expects an international number as a value, so make sure we always pass it one.
  const internationalNumberValue = useMemo(() => parsePhoneNumber(phoneNumberValue, 'AU')?.number, [phoneNumberValue]);

  return (
    <PhoneInput
      {...rest}
      international={false}
      country="AU"
      value={internationalNumberValue}
      onChange={setValue}
      formikField={formikField}
      inputComponent={CodeInputForwardRef}
    />
  );
}
